import { Component, Watch, Vue } from "vue-property-decorator";
import { TranslateResult } from "vue-i18n";
import { ACTIONS, StateCurrency } from "@/store";
import { getUserBranding, hasBranding } from "@/helpers/branding";

interface VueCustomSelect extends Vue {
  open: boolean;
  setCurrency: Function;
}

type UserSelectType = {
  label: TranslateResult;
  url: string;
};

@Component({
  components: {
    vSelect: () => import("vue-select"),
    MyReservation: () => import(
      /* webpackChunkName: "hero" */ "@/components/MyReservation/Menu/MyReservation.vue"
    )
  }
})
export default class Menu extends Vue {
  open = false;
  currency = {};

  get branding() {
    return getUserBranding(this.$store.state.user);
  }

  get hasBrandLogo(): boolean {
    return !!(this.branding && this.branding.logo);
  }

  /** Cuenta con marca propia: se esconden las piezas de Cardinal. */
  get isBranded(): boolean {
    return hasBranding(this.$store.state.user);
  }

  get headerLogo(): string {
    return this.branding?.logo || require("@/assets/header/logo.svg");
  }

  get headerLogoMobile(): string {
    return (
      this.branding?.logoMobile ||
      this.branding?.logo ||
      require("@/assets/header/logo-mobile.svg")
    );
  }

  get selectUserOptions(): Array<UserSelectType> {
    if (!(this.$store.state.user && this.$store.state.user.id)) return [];

    return [
      {
        label: this.$t("Menu.reservations"),
        url: `/reservations`
      },
      {
        label: this.$t("Menu.info"),
        url: `/agencies`
      },
      {
        label: this.$t("Menu.users"),
        url: `/users`
      },
      {
        label: this.$t("Menu.logout"),
        url: "/logout"
      }
    ];
  }

  get backendUrl() {
    return `${process.env.VUE_APP_BACKEND_URL}login/agencies`;
  }

  @Watch("currency")
  onCurrencyChange(val: StateCurrency) {
    // Asegurarnos de que la moneda en el store sea la misma
    if (val && this.$store.state.currentCurrency.code !== val.code) {
      this.$store.dispatch(ACTIONS.SET_CURRENCY, val);
    }
  }

  @Watch("$store.state.currentCurrency")
  onCurrentCurrencyChange(newCurrency: StateCurrency) {
    this.currency = newCurrency;
  }


  isMobileViewport(): boolean {
    return window.matchMedia("(max-width: 1200px)").matches;
  }

  applyBotVisibilityForMenu() {

    if (!this.isMobileViewport()) {
      // Si estoy en desktop, lo muestro
      if (typeof (window as any).bmShow === "function") {
        (window as any).bmShow();
      }
      return;
    }

    // En mobile: si menú abierto => ocultar bot
    if (this.open) {
      if (typeof (window as any).bmHide === "function") {
        (window as any).bmHide();
      }
    } else {
      // Menú cerrado => volver a mostrar salvo ruta donde lo oculto
      const path = (this.$route.path || "").toLowerCase();
      const routeForcesHide = path.startsWith("/checkout");

      if (!routeForcesHide) {
        if (typeof (window as any).bmShow === "function") {
          (window as any).bmShow();
          if (typeof (window as any).bmMinimize === "function") {
            (window as any).bmMinimize();
          }
        }
      }
    }
  }

  @Watch("open")
  removeScroll() {
    const body: HTMLBodyElement | null | undefined = document.querySelector(
      "body"
    );
    if (this.open) {
      body!.style.overflow = "hidden";
    } else {
      body!.style.overflow = "auto";
    }

    this.applyBotVisibilityForMenu();
  }
  @Watch("$route")
  routeChangerHandler() {
    this.open = false;
    this.applyBotVisibilityForMenu();
  }

  mounted() {
		/* Analytics */
		if (this.$store.state.user) {
			(window as any).dataLayer.push({'event': 'event', 'event_action': 'login', 'event_category' : 'engagement', 'event_label' : this.$store.state.user && this.$store.state.user.email});
			(window as any).dataLayer.push({'userId': this.$store.state.user && this.$store.state.user.id});
		}

    this.$nextTick(() => {
      this.loadCurrency();
      this.addScrollMenuEvent();
      this.applyBotVisibilityForMenu();
	});
  }

  loadCurrency() {
    if (!this.$store.state.currentCurrency.code) {
      let currency = {
        code: "USD",
        createdAt: "",
        id: 1,
        markup: 0,
        usdPrice: 1
      };
      if (this.$store.state.pos && this.$store.state.pos.code === "AR") {
        currency = this.$store.state.currencies.find(
          (c: StateCurrency) => c.code === "ARS"
        );
      }
      this.$store.dispatch(ACTIONS.SET_CURRENCY, currency);
      this.currency = currency;
    } else {
      this.currency = this.$store.state.currentCurrency;
    }
  }

  addScrollMenuEvent() {
    console.log('');
  }

  toggleMenu() {
    this.open = !this.open;
  }

  selectUserChange(value: UserSelectType) {
    if (value.url === "/logout") this.logout();
    else this.goTo(value.url);
  }

  goTo(txt: string) {
    if (txt === "/reservations") window.location.href = `${process.env.VUE_APP_BACKEND_URL}reservas/1`;
    else if (txt === "/agencies") window.location.href = `/agencies`;
    else if (txt === "/users") window.location.href = `${process.env.VUE_APP_BACKEND_URL}usuarios/1`;
  }

  logout() {
    const branding = getUserBranding(this.$store.state.user);

    // Una agencia que entro desde el sitio de un socio no tiene contrasena, asi
    // que el login de agencias no le sirve de nada. Se lee antes de limpiar la
    // sesion.
    const viaSso = this.$store.state.user.viaSso;

    // Vuelve al login de agencias, no al home: quien cierra sesion en el
    // frontend es un operador, no un cliente final. Si su cuenta tiene marca
    // propia, el login se pinta con ella en lugar de la de Cardinal.
    const brand =
      branding && branding.slug
        ? `?brand=${encodeURIComponent(branding.slug)}`
        : "";

    // Solo se borra la sesion persistida, sin vaciar el store: la pagina se
    // reemplaza igual, y limpiar el estado reactivo aca hacia que Vue repintara
    // todo sin marca por un instante antes de que arranque la navegacion.
    this.$store.dispatch(ACTIONS.CLEAR_SESSION);

    // Navegacion dura, igual que el resto de los caminos de perdida de sesion
    // (`main.ts`, `SearchBar`): tira la cache de Apollo y corta las queries
    // autenticadas que quedaron en vuelo.
    window.location.href = viaSso
      ? `${process.env.VUE_APP_BACKEND_URL}session-ended`
      : `${this.backendUrl}${brand}`;
  }

  doLogin() {
    window.location.href = this.backendUrl;
  }


}
